import { ActionTree, GetterTree, Module, MutationTree } from "vuex";
import { IPositionState } from "./types";
import { IIDAStore } from "../types";
import { Position } from "../../models/positions";
import { positionService } from "../../services/Position.service";
import { PositionCategory } from "../../models/positionCategories";
import { SalaryStillingskategori } from "@/models/salaryStillingskategorier";

export const getInitialState = (): IPositionState => {
  return {
    positions: [],
    positionCategories: [],
    salaryStillingskategorier: []
  };
};

const namespaced = true;

export const getters: GetterTree<IPositionState, IIDAStore> = {
  GetPositions: (state) => {
    return state.positions;
  },
  GetPositionById: (state) => (positionId: number) => {
    return state.positions.find((position: Position) => position.id === positionId);
  },
  GetPositionCategories: (state) => {
    return state.positionCategories;
  },
  GetPositionCategoryByPositionId: (state) => (positionId: number) => {
    const position = state.positions.find((p) => p.id === positionId);
    if (position) {
      return state.positionCategories.find((p) => p.id === position.position_category_id);
    } else {
      return undefined;
    }
  },
  GetPositionCategoryById: (state) => (positionCategoryId: number) => {
    return state.positionCategories.find(
      (positionCategory: PositionCategory) => positionCategory.id === positionCategoryId
    );
  },
  GetSalaryStillingskategorier: (state) => {
    return state.salaryStillingskategorier;
  }
};

export const actions: ActionTree<IPositionState, IIDAStore> = {
  reset({ commit }) {
    commit("reset");
  },
  fetchPositions({ commit, state }): Promise<Position[]> {
    return new Promise((resolve, reject) => {
      if (state.positions.length === 0) {
        try {
          positionService.fetchPositions().then((positions) => {
            commit("updatePositions", { positions });
            return resolve(positions);
          });
        } catch (error) {
          return reject(error);
        }
      } else {
        resolve(state.positions);
      }
    });
  },
  fetchPositionCategories({ commit, state }): Promise<PositionCategory[]> {
    return new Promise((resolve, reject) => {
      if (state.positionCategories.length === 0) {
        try {
          positionService.fetchPositionCategories().then((positionCategories) => {
            commit("updatePositionCategories", { positionCategories });
            return resolve(positionCategories);
          });
        } catch (error) {
          return reject(error);
        }
      } else {
        resolve(state.positionCategories);
      }
    });
  },
  createPosition({ commit }, position: Position): Promise<Position> {
    return new Promise((resolve, reject) => {
      positionService
        .createPosition(position)
        .then((newPosition) => {
          commit("insertPosition", newPosition);
          resolve(newPosition);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  updatePosition({ commit }, position: Position): Promise<Position> {
    return new Promise((resolve, reject) => {
      positionService
        .updatePosition(position)
        .then((updatedPosition) => {
          commit("updatePosition", updatedPosition);
          resolve(updatedPosition);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  deletePosition({ commit }, positionId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      positionService
        .deletePosition(positionId)
        .then((response) => {
          commit("deletePosition", positionId);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  createPositionCategory(
    { commit },
    positioncategory: PositionCategory
  ): Promise<PositionCategory> {
    return new Promise((resolve, reject) => {
      positionService
        .createPositionCategory(positioncategory)
        .then((newPositionCategory) => {
          commit("insertPositionCategory", newPositionCategory);
          resolve(newPositionCategory);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  deletePositionCategory({ commit }, positioncategoryId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      positionService
        .deletePositionCategory(positioncategoryId)
        .then((response) => {
          commit("deletePositionCategory", positioncategoryId);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
};

export const mutations: MutationTree<IPositionState> = {
  reset(state: IPositionState) {
    Object.assign(state, getInitialState());
  },
  insertPosition(state: IPositionState, position: Position) {
    state.positions.push(position);
  },
  updatePosition(state: IPositionState, position: Position) {
    const index = state.positions.findIndex((d) => d.id === position.id);
    state.positions.splice(index, 1);
    state.positions.push(position);
  },
  deletePosition(state: IPositionState, positionId: number) {
    const index = state.positions.findIndex((d) => d.id === positionId);
    state.positions.splice(index, 1);
  },
  updatePositions(state: IPositionState, payload: { positions: Position[]; from: Date; to: Date }) {
    state.positions = [
      ...state.positions,
      ...payload.positions.filter(
        (newPositions) => !state.positions.find((existing) => existing.equals(newPositions))
      )
    ];
  },
  insertPositionCategory(state: IPositionState, positionCategory: PositionCategory) {
    state.positionCategories.push(positionCategory);
  },
  updatePositionCategory(state: IPositionState, positionCategory: PositionCategory) {
    const index = state.positionCategories.findIndex((d) => d.id === positionCategory.id);
    state.positionCategories.splice(index, 1);
    state.positionCategories.push(positionCategory);
  },
  deletePositionCategory(state: IPositionState, positionCategoryId: number) {
    const index = state.positionCategories.findIndex((d) => d.id === positionCategoryId);
    state.positionCategories.splice(index, 1);
  },
  updatePositionCategories(
    state: IPositionState,
    payload: { positionCategories: PositionCategory[] }
  ) {
    state.positionCategories = [
      ...state.positionCategories,
      ...payload.positionCategories.filter(
        (newPositionCategories) =>
          !state.positionCategories.find((existing) => existing.equals(newPositionCategories))
      )
    ];
  },
  updateSalaryStillingskategorier(
    state: IPositionState,
    payload: { salaryStillingskategorier: SalaryStillingskategori[] }
  ) {
    state.salaryStillingskategorier = [
      ...state.salaryStillingskategorier,
      ...payload.salaryStillingskategorier.filter(
        (newSalaryStillingskategorier) =>
          !state.salaryStillingskategorier.find((existing) =>
            existing.equals(newSalaryStillingskategorier)
          )
      )
    ];
  }
};

export const positionsStoreModule: Module<IPositionState, IIDAStore> = {
  namespaced: namespaced,
  state: getInitialState(),
  getters: getters,
  actions: actions,
  mutations: mutations
};
